import axios, { AxiosPromise, Method } from 'axios'

type bodyType = Record<string, unknown>
export default class RequestHandler {
  token: string
  url: string
  method: Method
  body: bodyType | FormData
  multipart: boolean

  constructor(url: string, method: Method, body?: bodyType, multipart = false) {
    this.token = RequestHandler.getToken()
    this.url = url
    this.method = method
    this.multipart = multipart
    this.body = this.wrapBody(body)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request<T = any>(): AxiosPromise<T> {
    const headers: Record<string, string> = {
      'X-CSRF-Token': this.token,
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (this.multipart) {
      headers['Content-Type'] = 'multipart/form-data'
    } else {
      headers['Content-Type'] = 'application/json; charset=utf-8'
    }

    return axios.request({
      url: this.url,
      method: this.method,
      headers: headers,
      data: this.body,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async requestWithBody(): Promise<Record<string, any>> {
    const response = await this.request()
    return {
      status: response.status,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      body: response.data,
    }
  }

  static getToken(): string {
    const tokenMeta: HTMLMetaElement = document.getElementsByName(
      'csrf-token'
    )[0] as HTMLMetaElement
    return tokenMeta?.content || ''
  }

  static toQueryString(from: Record<string, unknown>): string {
    const queries = []
    for (const key in from) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      queries.push(`${RequestHandler.toSnakeCase(key)}=${from[key]}`) // eslint-disable-line
    }
    return queries.join('&')
  }

  static toSnakeCase(from: string): string {
    return from.replace(/([A-Z])/g, (s) => '_' + s.charAt(0).toLowerCase());
  }

  wrapBody(body: bodyType): bodyType | FormData {
    if (!this.multipart) {
      return body
    }

    const formData = new FormData()
    for (const key in body) {
      const value = body[key]
      if (Array.isArray(value)) {
        // Handle arrays by appending each element with the same key
        value.forEach((item) => {
          formData.append(`${key}[]`, String(item))
        })
      } else if (value instanceof File || value instanceof Blob) {
        // Handle File and Blob objects properly for multipart uploads
        formData.set(key, value)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        formData.set(key, typeof value === 'string' ? value : String(value))
      }
    }
    return formData
  }
}
